/**
 * SubmissionForm Component
 *
 * Minimal, high-conversion form for startup pitch deck submissions:
 * - Company name, email, optional name fields
 * - PDF file picker (web drag-drop + native document picker)
 * - Consent checkbox with terms link
 * - Submit button with progress stages
 * - Inline validation with shake animation
 */

import { useState, useRef } from 'react';
import {
  View,
  Text,
  TextInput,
  Platform,
} from 'react-native';
import { openExternalUrl } from '@/lib/linking';
import { TurnstileWidget, TURNSTILE_SITE_KEY } from '@/components/submission/TurnstileWidget';
import { Upload, FileText, X, Check } from 'lucide-react-native';
import * as DocumentPicker from 'expo-document-picker';
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  withTiming,
  withSequence,
  FadeInDown,
} from 'react-native-reanimated';
import AnimatedPressable from '@/components/AnimatedPressable';
import { usePublicThemeColors } from '@/lib/useThemeColors';
import { cn } from '@/lib/cn';
import { webStyle, noOutlineStyle } from '@/lib/platform';
import type { SubmissionFormData, SubmissionStage } from '@/lib/submission/use-submit-deck';

// ============================================================================
// Types
// ============================================================================

interface SubmissionFormProps {
  slug: string;
  investorName: string;
  onSubmit: (data: SubmissionFormData) => Promise<void>;
  isSubmitting: boolean;
  stage: SubmissionStage;
  progress: number;
}

interface SelectedFile {
  uri: string;
  name: string;
  size: number | null | undefined;
}

interface ValidationErrors {
  companyName?: string;
  contactEmail?: string;
  contactName?: string;
  file?: string;
  consent?: string;
}

// ============================================================================
// Helpers
// ============================================================================

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function formatFileSize(bytes: number | null | undefined): string {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function getStageLabel(stage: SubmissionStage): string {
  switch (stage) {
    case 'reading-file':
      return 'Preparing file...';
    case 'uploading':
      return 'Uploading deck...';
    case 'processing':
      return 'AI is analyzing...';
    default:
      return 'Submit Pitch Deck';
  }
}

// ============================================================================
// FormField Component
// ============================================================================

function FormField({
  label,
  optional,
  required,
  error,
  children,
}: {
  label: string;
  optional?: boolean;
  required?: boolean;
  error?: string;
  children: React.ReactNode;
}) {
  const colors = usePublicThemeColors();
  return (
    <View className="mb-4">
      <View className="flex-row items-center mb-1.5">
        <Text
          className="text-sm font-medium"
          style={{ color: colors.text.primary }}
        >
          {label}
        </Text>
        {required && (
          <Text
            className="text-sm ml-0.5"
            style={{ color: colors.red.main }}
            accessibilityLabel="required"
          >
            *
          </Text>
        )}
        {optional && (
          <Text
            className="text-xs ml-1.5"
            style={{ color: colors.text.tertiary }}
          >
            (optional)
          </Text>
        )}
      </View>
      {children}
      {error ? (
        <Text
          className="text-xs mt-1"
          style={{ color: colors.red.main }}
        >
          {error}
        </Text>
      ) : null}
    </View>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export function SubmissionForm({
  slug,
  investorName,
  onSubmit,
  isSubmitting,
  stage,
  progress,
}: SubmissionFormProps) {
  const colors = usePublicThemeColors();

  // Form state
  const [companyName, setCompanyName] = useState<string>('');
  const [contactEmail, setContactEmail] = useState<string>('');
  const [contactName, setContactName] = useState<string>('');
  const [submitterNote, setSubmitterNote] = useState<string>('');
  const SUBMITTER_NOTE_MAX = 400;
  const [selectedFile, setSelectedFile] = useState<SelectedFile | null>(null);
  const [consentGiven, setConsentGiven] = useState<boolean>(false);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [errors, setErrors] = useState<ValidationErrors>({});

  // Focus states
  const [focusedField, setFocusedField] = useState<string | null>(null);

  // Shake animation
  const shakeX = useSharedValue(0);
  const shakeStyle = useAnimatedStyle(() => ({
    transform: [{ translateX: shakeX.value }],
  }));

  // Progress bar animation
  const progressWidth = useSharedValue(0);
  const progressStyle = useAnimatedStyle(() => ({
    width: `${progressWidth.value}%`,
  }));

  // Update progress bar when progress changes
  if (isSubmitting) {
    progressWidth.value = withTiming(progress, { duration: 300 });
  } else {
    progressWidth.value = 0;
  }

  // Refs for input focus management
  const emailRef = useRef<TextInput>(null);
  const nameRef = useRef<TextInput>(null);

  // Input style helper
  const getInputStyle = (fieldName: string) => {
    const isFocused = focusedField === fieldName;
    const hasError = errors[fieldName as keyof ValidationErrors];
    return {
      backgroundColor: colors.surface.primary,
      borderWidth: 1,
      borderColor: hasError
        ? colors.red.main
        : isFocused
          ? colors.primary[500]
          : colors.border.light,
      borderRadius: 12,
      paddingHorizontal: 16,
      minHeight: 48,
      color: colors.text.primary,
      fontSize: 15,
    };
  };

  // ============================================================================
  // File Picker
  // ============================================================================

  const pickFile = async () => {
    try {
      const result = await DocumentPicker.getDocumentAsync({
        type: 'application/pdf',
        copyToCacheDirectory: true,
      });

      if (!result.canceled && result.assets?.[0]) {
        const file = result.assets[0];
        const sizeBytes = file.size ?? 0;

        if (sizeBytes > 20 * 1024 * 1024) {
          setErrors((prev) => ({
            ...prev,
            file: 'File is too large. Maximum size is 20MB.',
          }));
          return;
        }

        setSelectedFile({
          uri: file.uri,
          name: file.name,
          size: file.size,
        });
        setErrors((prev) => {
          const { file: _, ...rest } = prev;
          return rest;
        });
      }
    } catch {
      setErrors((prev) => ({
        ...prev,
        file: 'Failed to select file. Please try again.',
      }));
    }
  };

  const removeFile = () => {
    setSelectedFile(null);
  };

  // ============================================================================
  // Validation
  // ============================================================================

  const validate = (): boolean => {
    const newErrors: ValidationErrors = {};

    if (!companyName.trim()) {
      newErrors.companyName = 'Company name is required.';
    } else if (companyName.trim().length > 200) {
      newErrors.companyName = 'Company name is too long (max 200 characters).';
    }

    if (!contactEmail.trim()) {
      newErrors.contactEmail = 'Email is required.';
    } else if (!EMAIL_REGEX.test(contactEmail.trim())) {
      newErrors.contactEmail = 'Please enter a valid email address.';
    }

    if (!contactName.trim()) {
      newErrors.contactName = 'Your name is required.';
    }

    if (!selectedFile) {
      newErrors.file = 'Please select a pitch deck PDF.';
    }

    if (!consentGiven) {
      newErrors.consent = 'You must agree to the terms to submit.';
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      // Shake animation on validation error
      shakeX.value = withSequence(
        withTiming(10, { duration: 50 }),
        withTiming(-10, { duration: 50 }),
        withTiming(8, { duration: 50 }),
        withTiming(-8, { duration: 50 }),
        withTiming(4, { duration: 50 }),
        withTiming(0, { duration: 50 })
      );
      return false;
    }

    return true;
  };

  // ============================================================================
  // Submit Handler
  // ============================================================================

  const handleSubmit = async () => {
    if (isSubmitting) return;
    if (!validate()) return;

    await onSubmit({
      companyName: companyName.trim(),
      contactEmail: contactEmail.trim().toLowerCase(),
      contactName: contactName.trim() || undefined,
      submitterNote: submitterNote.trim() || undefined,
      consentGiven: true,
      fileUri: selectedFile!.uri,
      fileName: selectedFile!.name,
      slug,
      captchaToken: captchaToken || undefined,
    });
  };

  // Check if form is complete (for button disabled state).
  // On web, require CAPTCHA token (unless site key not configured).
  // Localhost http:// dev: the Turnstile iframe (HTTPS) can't message back to
  // an HTTP parent due to browser protocol-mismatch policy. Skip the widget
  // entirely in this case — the edge function recognizes localhost origins
  // and bypasses Turnstile validation. Production https:// hosts work normally.
  const isLocalhostHttpDev =
    Platform.OS === 'web' &&
    typeof window !== 'undefined' &&
    window.location?.protocol === 'http:' &&
    (window.location?.hostname === 'localhost' || window.location?.hostname === '127.0.0.1');
  const isCaptchaRequired =
    Platform.OS === 'web' && !!TURNSTILE_SITE_KEY && !isLocalhostHttpDev;
  const isFormComplete =
    companyName.trim().length > 0 &&
    EMAIL_REGEX.test(contactEmail.trim()) &&
    selectedFile !== null &&
    consentGiven &&
    (!isCaptchaRequired || !!captchaToken);

  const isButtonDisabled = isSubmitting || !isFormComplete;

  return (
    <Animated.View entering={FadeInDown.delay(500).duration(500)} style={[shakeStyle]}>
      {/* Company Name */}
      <FormField label="Company Name" required error={errors.companyName}>
        <TextInput
          value={companyName}
          onChangeText={setCompanyName}
          placeholder="e.g. Acme Corp"
          placeholderTextColor={colors.text.tertiary}
          autoCapitalize="words"
          returnKeyType="next"
          onSubmitEditing={() => emailRef.current?.focus()}
          onFocus={() => setFocusedField('companyName')}
          onBlur={() => setFocusedField(null)}
          editable={!isSubmitting}
          style={noOutlineStyle(getInputStyle('companyName'))}
        />
      </FormField>

      {/* Email */}
      <FormField label="Your Email" required error={errors.contactEmail}>
        <TextInput
          ref={emailRef}
          value={contactEmail}
          onChangeText={setContactEmail}
          placeholder="founder@company.com"
          placeholderTextColor={colors.text.tertiary}
          keyboardType="email-address"
          autoCapitalize="none"
          autoComplete="email"
          returnKeyType="next"
          onSubmitEditing={() => nameRef.current?.focus()}
          onFocus={() => setFocusedField('contactEmail')}
          onBlur={() => setFocusedField(null)}
          editable={!isSubmitting}
          style={noOutlineStyle(getInputStyle('contactEmail'))}
        />
      </FormField>

      {/* Name (optional) */}
      <FormField label="Your Name" required error={errors.contactName}>
        <TextInput
          ref={nameRef}
          value={contactName}
          onChangeText={setContactName}
          placeholder="Jane Smith"
          placeholderTextColor={colors.text.tertiary}
          autoCapitalize="words"
          returnKeyType="done"
          onFocus={() => setFocusedField('contactName')}
          onBlur={() => setFocusedField(null)}
          editable={!isSubmitting}
          style={noOutlineStyle(getInputStyle('contactName'))}
        />
      </FormField>

      {/* Submitter Note (optional, 400 chars) */}
      <FormField label="A short note" optional>
        <TextInput
          value={submitterNote}
          onChangeText={(t) => setSubmitterNote(t.slice(0, SUBMITTER_NOTE_MAX))}
          placeholder={`How did you find ${investorName}? Why are you reaching out? (e.g. "We met at a pitch event last week", "Found you via a portfolio company")`}
          placeholderTextColor={colors.text.tertiary}
          multiline
          numberOfLines={4}
          maxLength={SUBMITTER_NOTE_MAX}
          onFocus={() => setFocusedField('submitterNote')}
          onBlur={() => setFocusedField(null)}
          editable={!isSubmitting}
          style={noOutlineStyle({
            ...getInputStyle('submitterNote'),
            minHeight: 96,
            paddingTop: 12,
            paddingBottom: 12,
            textAlignVertical: 'top',
          })}
        />
        <View style={{ alignItems: 'flex-end', marginTop: 4 }}>
          <Text
            className="text-xs"
            style={{
              color:
                submitterNote.length > SUBMITTER_NOTE_MAX * 0.9
                  ? colors.amber.main
                  : colors.text.tertiary,
            }}
          >
            {submitterNote.length}/{SUBMITTER_NOTE_MAX}
          </Text>
        </View>
      </FormField>

      {/* File Picker */}
      <FormField label="Pitch Deck" required error={errors.file}>
        {selectedFile ? (
          /* File selected state */
          <View
            className="flex-row items-center p-4 rounded-xl"
            style={{
              backgroundColor: colors.surface.primary,
              borderWidth: 1,
              borderColor: colors.primary[500],
            }}
          >
            <View
              className="w-10 h-10 rounded-lg items-center justify-center mr-3"
              style={{ backgroundColor: colors.primary[50] }}
            >
              <FileText size={20} color={colors.primary[600]} />
            </View>
            <View className="flex-1">
              <Text
                className="text-sm font-medium"
                style={{ color: colors.text.primary }}
                numberOfLines={1}
              >
                {selectedFile.name}
              </Text>
              {selectedFile.size ? (
                <Text
                  className="text-xs mt-0.5"
                  style={{ color: colors.text.tertiary }}
                >
                  {formatFileSize(selectedFile.size)}
                </Text>
              ) : null}
            </View>
            <AnimatedPressable
              onPress={removeFile}
              disabled={isSubmitting}
              className="w-8 h-8 rounded-full items-center justify-center"
              style={{ backgroundColor: colors.surface.tertiary }}
            >
              <X size={16} color={colors.text.secondary} />
            </AnimatedPressable>
          </View>
        ) : (
          /* File picker zone */
          <AnimatedPressable
            onPress={pickFile}
            disabled={isSubmitting}
            className="items-center py-8 rounded-xl"
            style={webStyle(
              {
                borderWidth: 2,
                borderStyle: 'dashed' as any,
                borderColor: errors.file ? colors.red.main : colors.border.medium,
                backgroundColor: colors.surface.primary,
              },
              { cursor: 'pointer' }
            )}
          >
            <View
              className="w-12 h-12 rounded-full items-center justify-center mb-3"
              style={{ backgroundColor: colors.primary[50] }}
            >
              <Upload size={22} color={colors.primary[600]} />
            </View>
            <Text
              className="text-sm font-medium mb-1"
              style={{ color: colors.text.primary }}
            >
              {Platform.OS === 'web'
                ? 'Drop your pitch deck here or click to browse'
                : 'Tap to select your pitch deck'}
            </Text>
            <Text
              className="text-xs"
              style={{ color: colors.text.tertiary }}
            >
              PDF, max 20MB
            </Text>
          </AnimatedPressable>
        )}
      </FormField>

      {/* Consent Checkbox */}
      <View className="mb-6">
        <AnimatedPressable
          onPress={() => {
            setConsentGiven(!consentGiven);
            if (errors.consent) {
              setErrors((prev) => {
                const { consent: _, ...rest } = prev;
                return rest;
              });
            }
          }}
          disabled={isSubmitting}
          className="flex-row items-start gap-3"
        >
          {/* Custom checkbox */}
          <View
            className={cn(
              'w-5 h-5 rounded items-center justify-center mt-0.5',
            )}
            style={{
              borderWidth: consentGiven ? 0 : 1.5,
              borderColor: errors.consent ? colors.red.main : colors.border.medium,
              backgroundColor: consentGiven ? colors.primary[600] : 'transparent',
            }}
          >
            {consentGiven && <Check size={14} color="#FFFFFF" strokeWidth={3} />}
          </View>
          <Text className="flex-1 text-sm leading-5" style={{ color: colors.text.secondary }}>
            I consent to AI analysis of my pitch deck and agree to the{' '}
            <Text
              onPress={() =>
                openExternalUrl(
                  Platform.OS === 'web' ? '/terms' : 'https://inverio.ai/terms',
                  'SubmissionForm.terms',
                )
              }
              style={{ color: colors.primary[600], textDecorationLine: 'underline' }}
            >
              Terms of Service
            </Text>
          </Text>
        </AnimatedPressable>
        {errors.consent ? (
          <Text className="text-xs mt-1 ml-8" style={{ color: colors.red.main }}>
            {errors.consent}
          </Text>
        ) : null}
      </View>

      {/* Turnstile CAPTCHA — only renders on web, and not on http://localhost
          (the HTTPS iframe can't message back to an HTTP parent). */}
      {TURNSTILE_SITE_KEY && !isLocalhostHttpDev ? (
        <View className="mb-4">
          <TurnstileWidget
            siteKey={TURNSTILE_SITE_KEY}
            onVerify={(token) => setCaptchaToken(token)}
            onError={() => setCaptchaToken(null)}
            onExpire={() => setCaptchaToken(null)}
            size="normal"
            // Web-only widget on a surface that's force-dark on web — match it
            // so the captcha box doesn't render light against the dark form.
            theme="dark"
          />
        </View>
      ) : null}

      {/* Submit Button */}
      <AnimatedPressable
        onPress={handleSubmit}
        disabled={isButtonDisabled}
        className="w-full rounded-xl items-center justify-center overflow-hidden"
        style={{
          backgroundColor: isButtonDisabled ? colors.border.medium : colors.primary[600],
          minHeight: 52,
        }}
      >
        <Text
          className="text-base font-semibold"
          style={{
            color: isButtonDisabled ? colors.text.tertiary : '#FFFFFF',
          }}
        >
          {getStageLabel(stage)}
        </Text>
      </AnimatedPressable>

      {/* Progress Bar (visible during submission) */}
      {isSubmitting && (
        <View
          className="mt-3 rounded-full overflow-hidden"
          style={{
            height: 4,
            backgroundColor: colors.surface.tertiary,
          }}
        >
          <Animated.View
            style={[
              progressStyle,
              {
                height: 4,
                borderRadius: 2,
                backgroundColor: colors.primary[500],
              },
            ]}
          />
        </View>
      )}
    </Animated.View>
  );
}
